import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, HelpCircle, Zap, Shield, BarChart2 } from 'lucide-react';
import { theme } from '../styles/theme';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  icon: React.ReactNode;
  items: FaqItem[];
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    title: 'Getting Started',
    icon: <Zap className="w-5 h-5" />,
    items: [
      {
        question: 'How do I start a new processing job?',
        answer:
          'Navigate to "Select Pipeline" in the sidebar, choose the pipeline that matches your data (Dimensions, Material, Part Category, or Generic), upload your CSV file and configure the job settings.',
      },
      {
        question: 'What file formats are supported?',
        answer:
          'CSV files (.csv) are supported. The system automatically detects UTF-8 and Latin-1 encodings. Files must have column headers in the first row.',
      },
      {
        question: 'How long does processing take?',
        answer:
          'Processing time depends on the number of rows and selected AI model. As a rough guide: ~1–2 seconds per row for standard models. Large jobs (10,000+ rows) may take 15–30 minutes.',
      },
    ],
  },
  {
    title: 'DigiPART Connection',
    icon: <Shield className="w-5 h-5" />,
    items: [
      {
        question: 'What does "Job Paused" mean?',
        answer:
          'A job is paused when the DigiPART session expires mid-run. Your AI results are safely checkpointed. Go to your Profile page, reconnect your DigiPART session, then return to the job and click "Resume Job".',
      },
      {
        question: 'How do I reconnect my DigiPART session?',
        answer:
          'Go to Profile → DigiPART Connection and enter your DigiPART password. The session is valid for the duration configured by your administrator (typically 8 hours).',
      },
      {
        question: 'Will I be charged again for a resumed job?',
        answer: 'No. Resuming a job picks up from the last saved checkpoint. You are only charged for AI calls that have not yet been completed.',
      },
    ],
  },
  {
    title: 'Pipelines & AI',
    icon: <Zap className="w-5 h-5" />,
    items: [
      {
        question: 'What is the Dimensions pipeline?',
        answer:
          'The Dimensions pipeline connects to DigiPART to fetch part data, uses AI to validate and normalise dimension values against engineering standards, then writes the verified dimensions back to DigiPART.',
      },
      {
        question: 'What does "Too Many Requests" mean?',
        answer:
          'This error means the AI service is temporarily rate-limited. The system will automatically retry with delays (30 s → 60 s → 120 s). If the error persists, wait a few minutes before retrying.',
      },
      {
        question: 'Which AI model should I choose?',
        answer:
          'For most jobs, Gemini 2.0 Flash offers the best speed/cost balance. For complex dimension extraction with unusual shapes, Gemini 1.5 Pro provides higher accuracy at a higher cost.',
      },
    ],
  },
  {
    title: 'Analytics & Costs',
    icon: <BarChart2 className="w-5 h-5" />,
    items: [
      {
        question: 'Where can I see my AI costs?',
        answer:
          'Each job detail page shows the total cost in SGD and USD. The Analytics page shows cost trends over time, broken down by pipeline type and user.',
      },
      {
        question: 'How is cost calculated?',
        answer:
          'Cost is calculated from the number of input and output tokens sent to the AI model. Pricing follows Google Gemini published rates (see the model pricing table in Analytics). Costs are converted to SGD at live exchange rates.',
      },
    ],
  },
];

const Docs: React.FC = () => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set([FAQ_SECTIONS[0].title]));
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleSection = (title: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const toggleItem = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-3">
        <BookOpen className={`w-8 h-8 text-primary`} />
        <div>
          <h2 className={`text-3xl font-bold ${theme.text.primary}`}>Help &amp; FAQ</h2>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="space-y-4">
        {FAQ_SECTIONS.map((section) => {
          const sectionOpen = openSections.has(section.title);
          return (
            <div
              key={section.title}
              className={`${theme.card.background} rounded-lg shadow-sm border border-border overflow-hidden`}
            >
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.title)}
                className={`w-full flex items-center justify-between px-6 py-4 text-left hover:bg-bg-base/40 transition-colors`}
                aria-expanded={sectionOpen}
                data-testid={`faq-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-primary">{section.icon}</span>
                  <span className={`font-semibold ${theme.text.primary}`}>{section.title}</span>
                </div>
                {sectionOpen ? (
                  <ChevronDown className={`w-5 h-5 ${theme.text.secondary}`} />
                ) : (
                  <ChevronRight className={`w-5 h-5 ${theme.text.secondary}`} />
                )}
              </button>

              {/* Items */}
              {sectionOpen && (
                <div className="border-t border-border divide-y divide-border-default">
                  {section.items.map((item) => {
                    const key = `${section.title}-${item.question}`;
                    const itemOpen = openItems.has(key);
                    return (
                      <div key={key}>
                        <button
                          onClick={() => toggleItem(key)}
                          className={`w-full flex items-start justify-between px-6 py-4 text-left hover:bg-bg-base/30 transition-colors gap-4`}
                          aria-expanded={itemOpen}
                          data-testid={`faq-question`}
                        >
                          <div className="flex items-start gap-2">
                            <HelpCircle className={`w-4 h-4 mt-0.5 shrink-0 ${theme.text.secondary}`} />
                            <span className={`text-sm font-medium ${theme.text.primary}`}>{item.question}</span>
                          </div>
                          {itemOpen ? (
                            <ChevronDown className={`w-4 h-4 shrink-0 mt-0.5 ${theme.text.secondary}`} />
                          ) : (
                            <ChevronRight className={`w-4 h-4 shrink-0 mt-0.5 ${theme.text.secondary}`} />
                          )}
                        </button>
                        {itemOpen && (
                          <div className={`px-6 pb-4 pl-12 text-sm ${theme.text.secondary} leading-relaxed`}>
                            {item.answer}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Docs;
